/**
 * Ground -> HUD pixels for the in-world indicators (08 §2.7). Pure.
 *
 * The app hands the HUD a projector: a ground point (x, z) to viewport CSS pixels, or null when
 * the point is off the image. The camera is orthographic, so that map is affine; sampling it at
 * the ranger and a few metres east and south of him recovers the whole of it. With the basis the
 * HUD can place points the projector would refuse (a gunshot forty metres off-screen) and size
 * a noise radius in pixels, without knowing anything about the camera.
 */

/** Ground point -> viewport CSS pixels; null when it falls outside the game image. */
export type Projector = (x: number, z: number) => { x: number; y: number } | null;

/** Where `Hud.layout` put the HUD: its top-left in viewport pixels, and its pixel scale. */
export interface HudFrame {
  left: number;
  top: number;
  scale: number;
}

export interface Point {
  x: number;
  y: number;
}

/** An affine ground -> HUD map, in the HUD's 640x360 logical pixels. */
export interface GroundBasis {
  /** The ground point the basis was sampled at, and where it lands. */
  worldX: number;
  worldZ: number;
  origin: Point;
  /** Logical pixels per metre travelled east (+x) and south (+z). */
  east: Point;
  south: Point;
}

/** A long baseline keeps a projector that rounds to whole pixels from skewing far-off points. */
const PROBE_METRES = 4;

/** Viewport CSS pixels -> the HUD's logical pixels. */
export function toLogical(p: Point, frame: HudFrame): Point {
  return { x: (p.x - frame.left) / frame.scale, y: (p.y - frame.top) / frame.scale };
}

/** Pixels per metre along one ground axis; probes the other way when the first probe is off-screen. */
function axis(
  project: Projector,
  at: Point,
  world: { x: number; z: number },
  dx: number,
  dz: number,
  frame: HudFrame,
): Point | null {
  for (const sign of [1, -1]) {
    const hit = project(world.x + dx * PROBE_METRES * sign, world.z + dz * PROBE_METRES * sign);
    if (hit === null) continue;
    const p = toLogical(hit, frame);
    return { x: ((p.x - at.x) / PROBE_METRES) * sign, y: ((p.y - at.y) / PROBE_METRES) * sign };
  }
  return null;
}

/** The ground -> HUD map sampled around (x, z); null while that point is off the image. */
export function groundBasis(project: Projector, x: number, z: number, frame: HudFrame): GroundBasis | null {
  const centre = project(x, z);
  if (centre === null || !(frame.scale > 0)) return null;
  const origin = toLogical(centre, frame);
  const east = axis(project, origin, { x, z }, 1, 0, frame);
  const south = axis(project, origin, { x, z }, 0, 1, frame);
  if (east === null || south === null) return null;
  return { worldX: x, worldZ: z, origin, east, south };
}

/** Any ground point in HUD logical pixels, on the image or far off it. */
export function toHud(basis: GroundBasis, x: number, z: number): Point {
  const dx = x - basis.worldX;
  const dz = z - basis.worldZ;
  return {
    x: basis.origin.x + basis.east.x * dx + basis.south.x * dz,
    y: basis.origin.y + basis.east.y * dx + basis.south.y * dz,
  };
}

/** Semi-axes, in pixels, of a ground circle of `radius` metres (the camera's pitch squashes it). */
export function ringRadii(basis: GroundBasis, radius: number): { rx: number; ry: number } {
  return {
    rx: Math.hypot(basis.east.x, basis.east.y) * radius,
    ry: Math.hypot(basis.south.x, basis.south.y) * radius,
  };
}
